import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import { filesApi } from '../../api/files';
import EmojiPicker from './EmojiPicker';
import StickerPicker from './StickerPicker';
import GifPicker from './GifPicker';
import VoiceRecorder from './VoiceRecorder';
import MentionsDropdown from './MentionsDropdown';
import CreatePollModal from './CreatePollModal';
import { getAudioExtension, formatDuration } from '../../utils/waveform';
import '../../css/chats/SendMessageForm.css';

/**
 * Форма отправки сообщения
 * Отправляет текст и вложения через onSend(text, attachmentIds)
 * Также отправляет события typing через onTyping()
 * V2: поддерживает режимы ответа (reply) и редактирования (edit)
 *
 * @param {string} [chatId] - ID чата для привязки файлов (entityType=CHAT)
 * @param {Object} [replyToMessage] - Сообщение, на которое отвечаем
 * @param {Object} [editingMessage] - Сообщение, которое редактируем
 * @param {Function} [onCancelReply] - Отмена режима ответа
 * @param {Function} [onCancelEdit] - Отмена режима редактирования
 */
export default function SendMessageForm({
    onSend,
    onSendSticker,
    onSendGif,
    onCreatePoll,
    onTyping,
    onStopTyping,
    disabled = false,
    typingEnabled = true,
    className = "",
    chatId = null,
    participants = [],
    replyToMessage = null,
    editingMessage = null,
    onCancelReply = null,
    onCancelEdit = null,
    showPollButton = false
}) {
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showStickerPicker, setShowStickerPicker] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [showPollModal, setShowPollModal] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [sendingVoice, setSendingVoice] = useState(false);
    const inputId = 'send-message-input';
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const lastTypingSentRef = useRef(0);
    const draftSaveTimeoutRef = useRef(null);
    const isEditingRef = useRef(false);
    const emojiPickerRef = useRef(null);
    const emojiBtnRef = useRef(null);
    const stickerBtnRef = useRef(null);
    const gifBtnRef = useRef(null);
    const textareaRef = useRef(null);

    // === @mentions state ===
    const [mentionState, setMentionState] = useState({
        open: false,
        query: '',
        startIndex: -1,   // позиция символа @ в тексте
        selectedIndex: 0,  // выбранный элемент в дропдауне
    });
    const mentionStateRef = useRef(mentionState);
    mentionStateRef.current = mentionState;

    // === Авто-высота textarea (без ограничения) ===
    const autoResize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }, []);

    // === Черновики сообщений (localStorage) ===
    const getDraftKey = useCallback((id) => `hrnb:chat-draft:${id}`, []);

    const saveDraft = useCallback((text) => {
        if (!chatId) return;
        if (isEditingRef.current) return; // не сохраняем черновик в режиме редактирования
        if (draftSaveTimeoutRef.current) {
            clearTimeout(draftSaveTimeoutRef.current);
        }
        draftSaveTimeoutRef.current = setTimeout(() => {
            try {
                if (text.trim()) {
                    localStorage.setItem(getDraftKey(chatId), text);
                } else {
                    localStorage.removeItem(getDraftKey(chatId));
                }
            } catch { /* localStorage недоступен */ }
        }, 400);
    }, [chatId, getDraftKey]);

    const clearDraft = useCallback(() => {
        if (!chatId) return;
        if (isEditingRef.current) return; // не очищаем черновик при отправке редактирования
        // Отменяем pending debounce-сохранение, иначе оно перезапишет удаление
        if (draftSaveTimeoutRef.current) {
            clearTimeout(draftSaveTimeoutRef.current);
            draftSaveTimeoutRef.current = null;
        }
        try {
            localStorage.removeItem(getDraftKey(chatId));
        } catch { /* noop */ }
    }, [chatId, getDraftKey]);

    // Восстановление черновика при смене чата
    useEffect(() => {
        if (editingMessage) {
            isEditingRef.current = true;
            return;
        }
        isEditingRef.current = false;
        if (!chatId) {
            setMessage('');
            setTimeout(autoResize, 0);
            return;
        }
        try {
            const draft = localStorage.getItem(getDraftKey(chatId));
            setMessage(draft || '');
        } catch {
            setMessage('');
        }
        setTimeout(autoResize, 0);
    }, [chatId, editingMessage, getDraftKey, autoResize]);

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (draftSaveTimeoutRef.current) {
                clearTimeout(draftSaveTimeoutRef.current);
            }
        };
    }, []);

    // Устанавливаем фокус на инпут при монтировании
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    // V2: предзаполняем текст при редактировании
    useEffect(() => {
        if (editingMessage) {
            const text = editingMessage.content || editingMessage.text || '';
            setMessage(text);
            // Авто-высота при подстановке текста редактирования
            setTimeout(() => {
                autoResize();
                textareaRef.current?.focus();
            }, 0);
        }
    }, [editingMessage, autoResize]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        closeMentions();
        const hasText = message.trim().length > 0;
        const hasAttachments = attachments.length > 0;

        if ((hasText || hasAttachments) && !disabled && !uploading) {
            const sentText = message.trim();
            const sentAttachments = attachments.map(a => a.id);
            // onSend асинхронный — очищаем инпут и черновик только после успеха
            try {
                await onSend(sentText, sentAttachments);
                setMessage('');
                setAttachments([]);
                setUploadError(null);
                clearDraft();
                // Сбрасываем высоту textarea после отправки
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                }
            } catch {
                // Ошибка отправки — сохраняем текст и вложения, чтобы пользователь мог повторить
                return;
            }
            // Очищаем таймер typing при отправке
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
            }
            // Немедленно убираем индикатор "печатает" у собеседника
            if (onStopTyping) {
                onStopTyping();
            }
            // Возвращаем фокус после отправки
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 100);
        }
    };

    // === @mentions ===
    const updateMentionState = useCallback((text, cursorPos) => {
        if (!participants || participants.length === 0) {
            setMentionState({ open: false, query: '', startIndex: -1, selectedIndex: 0 });
            return;
        }
        const beforeCursor = text.slice(0, cursorPos);
        // Ищем последний @, перед которым пробел или начало строки
        const atIndex = beforeCursor.lastIndexOf('@');
        if (atIndex === -1 || (atIndex > 0 && beforeCursor[atIndex - 1] !== ' ' && beforeCursor[atIndex - 1] !== '\n')) {
            setMentionState(prev => prev.open ? { open: false, query: '', startIndex: -1, selectedIndex: 0 } : prev);
            return;
        }
        const query = beforeCursor.slice(atIndex + 1);
        // Если после @ есть пробел — не открываем
        if (query.includes(' ')) {
            setMentionState(prev => prev.open ? { open: false, query: '', startIndex: -1, selectedIndex: 0 } : prev);
            return;
        }
        setMentionState({ open: true, query, startIndex: atIndex, selectedIndex: 0 });
    }, [participants]);

    const closeMentions = useCallback(() => {
        setMentionState({ open: false, query: '', startIndex: -1, selectedIndex: 0 });
    }, []);

    const insertMention = useCallback((profile) => {
        const { startIndex } = mentionStateRef.current;
        if (startIndex < 0) return;
        const before = message.slice(0, startIndex);
        const after = message.slice(startIndex + 1 + mentionStateRef.current.query.length);
        const mentionText = `@${profile.displayName} `;
        const newText = before + mentionText + after;
        setMessage(newText);
        saveDraft(newText);
        closeMentions();
        autoResize();
        // Возвращаем фокус и ставим курсор после вставленного упоминания
        requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (el) {
                el.focus();
                const pos = before.length + mentionText.length;
                el.setSelectionRange(pos, pos);
            }
        });
    }, [message, saveDraft, closeMentions, autoResize]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setMessage(newValue);
        saveDraft(newValue);
        autoResize();

        // Детект @mentions
        updateMentionState(newValue, e.target.selectionStart);

        // Отправляем событие typing с debounce
        if (newValue.trim() && !disabled && onTyping && typingEnabled) {
            const now = Date.now();
            // Отправляем не чаще чем раз в 2 секунды
            if (now - lastTypingSentRef.current > 2000) {
                onTyping();
                lastTypingSentRef.current = now;
            }

            // Очищаем предыдущий таймер
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Сбрасываем флаг typing через 2 секунды после последней ввода
            typingTimeoutRef.current = setTimeout(() => {
                lastTypingSentRef.current = 0;
            }, 2000);
        }
    };

    const handleKeyDown = (e) => {
        // === @mentions keyboard navigation ===
        if (mentionState.open) {
            const filteredCount = participants.filter(p =>
                !mentionState.query || p.displayName?.toLowerCase().includes(mentionState.query.toLowerCase())
            ).slice(0, 8).length;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionState(prev => ({
                    ...prev,
                    selectedIndex: Math.min(prev.selectedIndex + 1, filteredCount - 1),
                }));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionState(prev => ({
                    ...prev,
                    selectedIndex: Math.max(prev.selectedIndex - 1, 0),
                }));
                return;
            }
            if (e.key === 'Enter' && filteredCount > 0) {
                e.preventDefault();
                const filtered = participants.filter(p =>
                    !mentionState.query || p.displayName?.toLowerCase().includes(mentionState.query.toLowerCase())
                ).slice(0, 8);
                const selected = filtered[mentionState.selectedIndex];
                if (selected) insertMention(selected);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                closeMentions();
                return;
            }
        }

        // Отправка по Enter (без Shift и Ctrl)
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
            e.preventDefault();
            handleSubmit(e);
            return;
        }

        // Ctrl+Enter — перенос строки
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            const el = textareaRef.current;
            if (!el) return;
            const start = el.selectionStart;
            const end = el.selectionEnd;
            const newText = message.slice(0, start) + '\n' + message.slice(end);
            setMessage(newText);
            saveDraft(newText);
            // Возвращаем курсор после вставленного \n
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start + 1, start + 1);
                autoResize();
            });
        }
    };

    /**
     * Оборачивает выделенный текст маркерами форматирования (*bold*, _italic_, `code`).
     * Если текст не выделен — вставляет пустые маркеры и ставит курсор между ними.
     */
    const wrapSelection = useCallback((marker) => {
        const input = textareaRef.current;
        if (!input || input.disabled) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selected = message.slice(start, end);
        const before = message.slice(0, start);
        const after = message.slice(end);

        const newText = before + marker + selected + marker + after;
        setMessage(newText);
        saveDraft(newText);
        autoResize();

        // Возвращаем фокус и восстанавливаем выделение
        requestAnimationFrame(() => {
            input.focus();
            if (selected.length > 0) {
                // Выделяем текст вместе с маркерами
                input.setSelectionRange(start, end + marker.length * 2);
            } else {
                // Курсор между маркерами
                input.setSelectionRange(start + marker.length, start + marker.length);
            }
        });
    }, [message, saveDraft, autoResize]);

    /**
     * Вставляет эмодзи в текущую позицию курсора в поле ввода.
     * Сохраняет фокус и устанавливает курсор после вставленного символа.
     */
    const handleEmojiSelect = useCallback((emoji) => {
        const input = textareaRef.current;
        if (!input || input.disabled) {
            // Если поле недоступно — добавляем в конец
            const newText = message + emoji;
            setMessage(newText);
            saveDraft(newText);
            autoResize();
            return;
        }

        const start = input.selectionStart ?? message.length;
        const end = input.selectionEnd ?? message.length;
        const newText = message.slice(0, start) + emoji + message.slice(end);
        setMessage(newText);
        saveDraft(newText);
        autoResize();

        // Возвращаем фокус и ставим курсор после вставленного эмодзи
        requestAnimationFrame(() => {
            input.focus();
            const cursorPos = start + emoji.length;
            input.setSelectionRange(cursorPos, cursorPos);
        });
    }, [message, saveDraft, autoResize]);

    /**
     * Обработка выбора стикера — отправляет сообщение со стикером.
     */
    const handleStickerSelect = useCallback((sticker) => {
        if (!sticker || disabled || uploading) return;
        onSendSticker?.(sticker);
    }, [disabled, uploading, onSendSticker]);

    /**
     * Обработка выбора GIF — отправляет сообщение с GIF-вложением.
     * @param {{ fileId: string, url: string, gifId: string, title: string }} gifData
     */
    const handleGifSelect = useCallback((gifData) => {
        if (!gifData || !gifData.fileId || disabled || uploading) return;
        onSendGif?.(gifData);
    }, [disabled, uploading, onSendGif]);

    /**
     * Обработка отправки голосового сообщения.
     * Загружает записанный blob как аудио-вложение и отправляет сообщение.
     */
    const handleVoiceSend = useCallback(async (blob, audioDuration) => {
        if (!blob || blob.size === 0 || disabled || sendingVoice) return;

        setSendingVoice(true);
        setUploadError(null);
        setIsRecording(false);

        try {
            // Blob приходит как WAV (audio/wav) после конвертации в VoiceRecorder
            const ext = blob.type.includes('wav') ? 'wav' : getAudioExtension(blob.type);
            const fileName = `Голосовое сообщение ${formatDuration(audioDuration)}.${ext}`;
            const file = new File([blob], fileName, { type: blob.type });

            console.log('[SendMessageForm] Uploading voice:', { type: blob.type, size: blob.size, ext, fileName });

            const uploadOptions = chatId ? { entityType: 'CHAT', entityId: chatId } : {};
            const response = await filesApi.uploadAudio(file, uploadOptions);
            const fileData = response?.file || response;
            const fileId = fileData?.id;

            console.log('[SendMessageForm] Upload response:', { fileId, mimeType: fileData?.mimeType, contentType: fileData?.contentType, type: fileData?.type });

            if (!fileId) {
                throw new Error('Сервер не вернул ID файла');
            }

            // Отправляем сообщение с аудио-вложением (без текста)
            await onSend('', [fileId]);
            if (onStopTyping) {
                onStopTyping();
            }
        } catch (err) {
            console.error('[SendMessageForm] Voice upload failed:', err.message || err);
            const errorMessage = err.response?.data?.message || err.message || 'Не удалось отправить голосовое сообщение';
            setUploadError(errorMessage);
        } finally {
            setSendingVoice(false);
        }
    }, [chatId, disabled, sendingVoice, onSend]);

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

        // Проверка размера файла (макс. 100 MB согласно настройкам сервера)
        const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB в байтах
        if (file.size > MAX_FILE_SIZE) {
            setUploadError(`Файл слишком большой. Размер: ${fileSizeMB} MB. Максимальный размер: 100 MB`);
            e.target.value = '';
            return;
        }

        // Сбрасываем input чтобы можно было выбрать тот же файл повторно
        e.target.value = '';

        setUploading(true);
        setUploadError(null);

        try {
            let response;
            let fileType;

            // Используем специализированные методы в зависимости от типа файла
            // Если указан chatId, используем entityType=CHAT для структурированного хранения
            const uploadOptions = chatId ? { entityType: 'CHAT', entityId: chatId } : {};

            if (file.type.startsWith('image/')) {
                fileType = 'IMAGE';
                response = await filesApi.uploadImage(file, uploadOptions);
            } else if (file.type.startsWith('audio/')) {
                fileType = 'AUDIO';
                response = await filesApi.uploadAudio(file, uploadOptions);
            } else if (file.type.startsWith('video/')) {
                fileType = 'VIDEO';
                response = await filesApi.uploadVideo(file, uploadOptions);
            } else {
                fileType = 'DOCUMENT';
                response = await filesApi.upload(file, { type: fileType, ...uploadOptions });
            }

            const fileData = response?.file || response;
            const fileId = fileData?.id;

            if (!fileId) {
                throw new Error('Сервер не вернул ID файла');
            }

            setAttachments(prev => [...prev, {
                id: fileId,
                name: file.name,
                type: fileType,
                size: file.size,
                contentType: file.type
            }]);
        } catch (err) {
            console.error('[SendMessageForm] Upload failed:', err.message || err);

            // Обрабатываем ошибку 413
            let errorMessage = 'Не удалось загрузить файл';

            if (err.response?.status === 413) {
                const serverMessage = err.response?.data?.message;
                errorMessage = serverMessage
                    ? `Ошибка сервера: ${serverMessage}`
                    : `Файл слишком большой. Размер: ${fileSizeMB} MB. Максимальный размер: 100 MB`;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setUploadError(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const canSend = (message.trim().length > 0 || attachments.length > 0) && !disabled && !uploading && !sendingVoice;

    const getAttachmentIcon = (type) => {
        switch (type) {
            case 'IMAGE': return '🖼️';
            case 'AUDIO': return '🎵';
            case 'VIDEO': return '🎬';
            default: return '📎';
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <form onSubmit={handleSubmit} className={`send-message-form ${className}`}>
            {/* V2: превью ответа на сообщение */}
            {replyToMessage && !editingMessage && (
                <div className="send-message-form__reply-preview">
                    <div className="send-message-form__reply-content">
                        <span className="send-message-form__reply-label">↩ Ответ:</span>
                        <span className="send-message-form__reply-author">
                            {replyToMessage.sender?.displayName || replyToMessage.senderDisplayName || 'Пользователь'}
                        </span>
                        <span className="send-message-form__reply-text">
                            {replyToMessage.isDeleted ? '[Сообщение удалено]' : (replyToMessage.content || '[Вложение]')}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="send-message-form__reply-cancel"
                        onClick={onCancelReply}
                        title="Отменить ответ"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* V2: превью редактирования */}
            {editingMessage && (
                <div className="send-message-form__edit-preview">
                    <div className="send-message-form__reply-content">
                        <span className="send-message-form__reply-label">✎ Редактирование:</span>
                        <span className="send-message-form__reply-text">
                            {editingMessage.content || '[Вложение]'}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="send-message-form__reply-cancel"
                        onClick={onCancelEdit}
                        title="Отменить редактирование"
                    >
                        ✕
                    </button>
                </div>
            )}

            <div className="send-message-form__main">
                <button
                    type="button"
                    className="send-message-form__attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploading || sendingVoice}
                    title="Прикрепить файл (макс. 100 MB)"
                >
                    {uploading ? <HrnbLoader size="sm" /> : '📎'}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={disabled || uploading}
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.zip"
                />
                {showPollButton && (
                    <button
                        type="button"
                        className="send-message-form__attach-btn"
                        onClick={() => setShowPollModal(true)}
                        disabled={disabled || uploading || sendingVoice}
                        title="Создать опрос"
                    >
                        📊
                    </button>
                )}
                {false && isRecording ? (
                    <VoiceRecorder
                        onSend={handleVoiceSend}
                        onCancel={() => setIsRecording(false)}
                        disabled={sendingVoice}
                    />
                ) : (
                    <>
                        <div className="send-message-form__input-wrapper">
                            {mentionState.open && (
                                <MentionsDropdown
                                    participants={participants}
                                    query={mentionState.query}
                                    selectedIndex={mentionState.selectedIndex}
                                    onSelect={insertMention}
                                    onClose={closeMentions}
                                    onHover={(index) => setMentionState(prev => ({ ...prev, selectedIndex: index }))}
                                />
                            )}
                            <div className="send-message-form__toolbar">
                                <button
                                    type="button"
                                    className="send-message-form__fmt-btn"
                                    onClick={() => wrapSelection('*')}
                                    disabled={disabled || uploading}
                                    title="Жирный (*текст*)"
                                >
                                    <strong>B</strong>
                                </button>
                                <button
                                    type="button"
                                    className="send-message-form__fmt-btn"
                                    onClick={() => wrapSelection('_')}
                                    disabled={disabled || uploading}
                                    title="Курсив (_текст_)"
                                >
                                    <em>I</em>
                                </button>
                                <button
                                    type="button"
                                    className="send-message-form__fmt-btn send-message-form__fmt-btn--mono"
                                    onClick={() => wrapSelection('`')}
                                    disabled={disabled || uploading}
                                    title="Код (`текст`)"
                                >
                                    {'</>'}
                                </button>
                                <button
                                    type="button"
                                    ref={emojiBtnRef}
                                    className={`send-message-form__fmt-btn send-message-form__fmt-btn--emoji ${showEmojiPicker ? 'send-message-form__fmt-btn--active' : ''}`}
                                    onClick={() => {
                                        setShowEmojiPicker(!showEmojiPicker);
                                        setShowStickerPicker(false);
                                    }}
                                    disabled={disabled || uploading}
                                    title="Эмодзи"
                                >
                                    😊
                                </button>
                                <button
                                    type="button"
                                    ref={stickerBtnRef}
                                    className={`send-message-form__fmt-btn send-message-form__fmt-btn--sticker ${showStickerPicker ? 'send-message-form__fmt-btn--active' : ''}`}
                                    onClick={() => {
                                        setShowStickerPicker(!showStickerPicker);
                                        setShowEmojiPicker(false);
                                        setShowGifPicker(false);
                                    }}
                                    disabled={disabled || uploading}
                                    title="Стикеры"
                                >
                                    🎨
                                </button>
                                <button
                                    type="button"
                                    ref={gifBtnRef}
                                    className={`send-message-form__fmt-btn send-message-form__fmt-btn--gif ${showGifPicker ? 'send-message-form__fmt-btn--active' : ''}`}
                                    onClick={() => {
                                        setShowGifPicker(!showGifPicker);
                                        setShowEmojiPicker(false);
                                        setShowStickerPicker(false);
                                    }}
                                    disabled={disabled || uploading}
                                    title="GIF"
                                >
                                    GIF
                                </button>
                            </div>
                            {showEmojiPicker && (
                                <div ref={emojiPickerRef} className="send-message-form__emoji-picker-wrapper">
                                    <EmojiPicker
                                        anchorRef={emojiBtnRef}
                                        mode="input"
                                        onSelect={handleEmojiSelect}
                                        onClose={() => setShowEmojiPicker(false)}
                                        className="send-message-form__emoji-picker"
                                    />
                                </div>
                            )}
                            {showStickerPicker && (
                                <StickerPicker
                                    anchorRef={stickerBtnRef}
                                    onSelect={handleStickerSelect}
                                    onClose={() => setShowStickerPicker(false)}
                                />
                            )}
                            {showGifPicker && (
                                <GifPicker
                                    anchorRef={gifBtnRef}
                                    onSelect={handleGifSelect}
                                    onClose={() => setShowGifPicker(false)}
                                />
                            )}
                            <textarea
                                ref={textareaRef}
                                id={inputId}
                                placeholder="Напишите сообщение..."
                                value={message}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onBlur={() => {
                                    // Закрываем mentions с задержкой, чтобы клик по элементу успел сработать
                                    setTimeout(() => closeMentions(), 150);
                                }}
                                disabled={disabled || uploading}
                                className="send-message-form__input send-message-form__textarea"
                                rows={1}
                            />
                        </div>
                        {false && (
                            <button
                                type="button"
                                className="send-message-form__voice-btn"
                                onClick={() => setIsRecording(true)}
                                disabled={disabled || uploading}
                                title="Записать голосовое сообщение"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                            </button>
                        )}
                    </>
                )}
                {!isRecording && (
                    <HrnbButton
                        type="submit"
                        disabled={!canSend}
                        className="send-message-form__button"
                    >
                        {sendingVoice ? <HrnbLoader size="sm" /> : (
                            <>
                                <svg className="send-message-form__send-icon" width="18" height="18"
                                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {editingMessage ? (
                                        <>
                                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                            <path d="m15 5 4 4" />
                                        </>
                                    ) : (
                                        <path d="m22 2-7 20-4-9-9-4Z" />
                                    )}
                                </svg>
                                {editingMessage ? 'Сохранить' : 'Отправить'}
                            </>
                        )}
                    </HrnbButton>
                )}
            </div>

            {/* Прикреплённые файлы */}
            {attachments.length > 0 && (
                <div className="send-message-form__attachments">
                    {attachments.map((att, idx) => (
                        <div key={`${att.id}-${idx}`} className="send-message-form__attachment">
                            <span className="send-message-form__attachment-icon">
                                {getAttachmentIcon(att.type)}
                            </span>
                            <div className="send-message-form__attachment-info">
                                <span className="send-message-form__attachment-name" title={att.name}>
                                    {att.name}
                                </span>
                                <span className="send-message-form__attachment-size">
                                    {formatFileSize(att.size)}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="send-message-form__attachment-remove"
                                onClick={() => removeAttachment(idx)}
                                title="Удалить"
                                disabled={disabled}
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {uploadError && (
                <div className="send-message-form__upload-error">{uploadError}</div>
            )}

            {showPollButton && showPollModal && (
                <CreatePollModal
                    open={showPollModal}
                    chatId={chatId}
                    onClose={() => setShowPollModal(false)}
                    onCreated={(poll) => {
                        setShowPollModal(false);
                        onCreatePoll?.(poll);
                    }}
                />
            )}
        </form>
    );
}
